import { ActionIcon, Box, Divider, Grid, Group, Stack } from '@mantine/core';
import { useMemo, useState } from 'react';
import { IconMapPin, IconNote, IconPlus, IconTrash } from '@tabler/icons-react';
import { ContactPoint, Address, Organization } from '@medplum/fhirtypes';
import { SystemIcon } from '../SystemIcon';
import { SmartEditableField, EditableField } from '@/components/EditableField';
import { ExpandableCard } from '@/components/ExpandableCard';
import { inferSystem, removeOrgTelecomAt, removeOrgAddressAt } from '@/services/organization';

function sortContactPointsBySystem(contactPoints: ContactPoint[]): ContactPoint[] {
  return [...contactPoints].sort((a, b) => {
    const order = { phone: 1, email: 2 };
    const aOrder = order[a.system as keyof typeof order] ?? 3;
    const bOrder = order[b.system as keyof typeof order] ?? 3;
    return aOrder - bOrder;
  });
}

type TelecomRowProps = {
  telecom: ContactPoint;
  index: number;
  editingValue: string;
  onEdit: (index: number, val: string, key: string) => void;
  onDelete: (index: number) => void;
  onChange: (val: string) => void;
  rowKey: string;
};

function TelecomRow({ telecom, index, editingValue, onEdit, onDelete, onChange, rowKey }: TelecomRowProps) {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <Box
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ opacity: isHovered ? 1 : 0.6, transition: 'opacity 0.2s ease-in-out' }}
    >
      <Group w="100%" justify="space-between">
        <Group style={{ flex: 1, minWidth: 0 }} gap="md" wrap="nowrap" my="auto">
          <SystemIcon
            system={inferSystem(editingValue) ?? telecom.system}
            value={editingValue}
            fallback={IconNote}
            color="var(--mantine-color-blue-7)"
          />
          <Box style={{ flex: 1 }}>
            <SmartEditableField
              value={telecom.value ?? ''}
              placeholder={`Set ${telecom.system ?? 'value'}`}
              inputProps={{ size: 'xs' }}
              textProps={{
                style: {
                  flex: 1,
                  fontWeight: 600,
                  color: isHovered ? 'var(--mantine-color-gray-6)' : 'black',
                  fontSize: '12px',
                  transition: 'color 0.2s ease-in-out',
                },
              }}
              editable={true}
              dynamicSystem={true}
              onChange={onChange}
              onSubmit={async (val) => onEdit(index, val, rowKey)}
            />
          </Box>
        </Group>
        <ActionIcon
          onClick={() => onDelete(index)}
          variant="subtle"
          color="red"
          size={16}
          radius="md"
          style={{
            opacity: isHovered ? 1 : 0,
            transition: 'opacity 0.2s ease-in-out',
            pointerEvents: isHovered ? 'auto' : 'none',
          }}
        >
          <IconTrash />
        </ActionIcon>
      </Group>
    </Box>
  );
}

type AddressRowProps = {
  address: Address;
  index: number;
  editingValue: string;
  onEdit: (index: number, val: string, key: string) => void;
  onDelete: (index: number) => void;
  onChange: (val: string) => void;
  rowKey: string;
};

function AddressRow({ address, index, editingValue, onEdit, onDelete, onChange, rowKey }: AddressRowProps) {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <Box
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ opacity: isHovered ? 1 : 0.6, transition: 'opacity 0.2s ease-in-out' }}
    >
      <Group w="100%" justify="space-between">
        <Group style={{ flex: 1, minWidth: 0 }} gap="md" wrap="nowrap">
          <SystemIcon
            system={undefined}
            value={editingValue}
            fallback={IconMapPin}
            color="var(--mantine-color-blue-7)"
          />
          <Box style={{ flex: 1 }}>
            <EditableField
              value={(address as any).text ?? ''}
              placeholder={'Set address'}
              inputProps={{ size: 'xs' }}
              textProps={{
                style: {
                  flex: 1,
                  fontWeight: 600,
                  color: isHovered ? 'var(--mantine-color-gray-6)' : 'black',
                  fontSize: '12px',
                  transition: 'color 0.2s ease-in-out',
                },
              }}
              editable={true}
              onChange={onChange}
              onSubmit={async (val) => onEdit(index, val, rowKey)}
            />
          </Box>
        </Group>
        <ActionIcon
          onClick={() => onDelete(index)}
          variant="subtle"
          color="red"
          size={16}
          radius="md"
          style={{
            opacity: isHovered ? 1 : 0,
            transition: 'opacity 0.2s ease-in-out',
            pointerEvents: isHovered ? 'auto' : 'none',
          }}
        >
          <IconTrash />
        </ActionIcon>
      </Group>
    </Box>
  );
}

export function PartnerAboutSection({
  organization,
  onChange,
}: {
  organization: Organization;
  onChange: (org: Organization) => void;
}) {
  const telecom = useMemo(() => sortContactPointsBySystem(organization.telecom || []), [organization.telecom]);

  const addresses = organization.address || [];

  // Add/edit state for telecom and addresses separately
  const [addingNewTelecom, setAddingNewTelecom] = useState(false);
  const [newTelecomValue, setNewTelecomValue] = useState('');
  const [addingNewAddress, setAddingNewAddress] = useState(false);
  const [newAddressValue, setNewAddressValue] = useState('');
  const [editingValues, setEditingValues] = useState<Record<string, string>>({});

  const deleteTelecomAt = async (index: number) => {
    onChange(removeOrgTelecomAt(organization, index));
  };

  const deleteAddressAt = async (index: number) => {
    onChange(removeOrgAddressAt(organization, index));
  };

  const addNewTelecom = async (value: string) => {
    const v = value.trim();
    if (!v) return;
    const system = inferSystem(v) ?? 'other';
    const nextTelecom = [...telecom];
    nextTelecom.push({ system: system as any, value: v, rank: nextTelecom.length + 1 });
    onChange({ ...organization, telecom: nextTelecom, address: addresses });
    setAddingNewTelecom(false);
  };

  const addNewAddress = async (value: string) => {
    const v = value.trim();
    if (!v) return;
    const nextAddresses = [...addresses];
    nextAddresses.push({ text: v } as any);
    onChange({ ...organization, telecom, address: nextAddresses });
    setAddingNewAddress(false);
  };

  // Edit handlers that do NOT move entries across sections
  const editTelecomAt = async (index: number, val: string, key: string) => {
    const inferred = inferSystem(val);
    const nextTelecom = [...telecom];
    const prev = nextTelecom[index];
    nextTelecom[index] = {
      ...prev,
      system: (inferred ?? prev.system ?? 'other') as any,
      value: val,
    } as any;
    onChange({ ...organization, telecom: nextTelecom, address: addresses });
    setEditingValues((prevState) => {
      const { [key]: _omit, ...rest } = prevState;
      return rest;
    });
  };

  const editAddressAt = async (index: number, val: string, key: string) => {
    const nextAddresses = [...addresses];
    nextAddresses[index] = { ...nextAddresses[index], text: val } as any;
    onChange({ ...organization, telecom, address: nextAddresses });
    setEditingValues((prevState) => {
      const { [key]: _omit, ...rest } = prevState;
      return rest;
    });
  };

  return (
    <>
      {/* Telecom section */}
      <ExpandableCard
        title="Contact Info"
        action={
          <ActionIcon
            variant="light"
            size="xs"
            onClick={(e) => {
              e.stopPropagation();
              setAddingNewTelecom((v) => !v);
            }}
            aria-label="Add contact info"
          >
            <IconPlus size={16} />
          </ActionIcon>
        }
      >
        <Stack gap="md">
          {telecom.map((t, index) => {
            const key = `telecom-${index}`;
            const value = editingValues[key] ?? t.value ?? '';
            return (
              <TelecomRow
                key={key}
                telecom={t}
                index={index}
                editingValue={value}
                rowKey={key}
                onEdit={editTelecomAt}
                onDelete={deleteTelecomAt}
                onChange={(val) => setEditingValues((prev) => ({ ...prev, [key]: val }))}
              />
            );
          })}

          {addingNewTelecom && (
            <Grid w="100%">
              <Grid.Col span={1} my="auto">
                <SystemIcon
                  system={inferSystem(newTelecomValue)}
                  value={newTelecomValue}
                  fallback={IconNote}
                  color="var(--mantine-color-blue-7)"
                />
              </Grid.Col>
              <Grid.Col span={11}>
                <SmartEditableField
                  value={newTelecomValue}
                  placeholder="Enter phone, email, website, etc."
                  inputProps={{ size: 'xs', w: '90%' }}
                  editable={true}
                  focus={addingNewTelecom}
                  dynamicSystem={true}
                  onChange={(v) => setNewTelecomValue(v)}
                  onSubmit={async (v) => {
                    await addNewTelecom(v);
                    setNewTelecomValue('');
                  }}
                  onCancel={() => {
                    setAddingNewTelecom(false);
                    setNewTelecomValue('');
                  }}
                  onEndEditing={(info) => {
                    if (!info.value) {
                      setAddingNewTelecom(false);
                      setNewTelecomValue('');
                    }
                  }}
                />
              </Grid.Col>
            </Grid>
          )}
        </Stack>
      </ExpandableCard>
      <Divider />

      {/* Addresses section */}
      <ExpandableCard
        title="Addresses"
        action={
          <ActionIcon
            variant="light"
            size="xs"
            onClick={(e) => {
              e.stopPropagation();
              setAddingNewAddress((v) => !v);
            }}
            aria-label="Add address"
          >
            <IconPlus size={16} />
          </ActionIcon>
        }
      >
        <Stack gap="md">
          {addresses.map((a, index) => {
            const key = `address-${index}`;
            const value = editingValues[key] ?? (a as any).text ?? '';
            return (
              <AddressRow
                key={key}
                address={a}
                index={index}
                editingValue={value}
                rowKey={key}
                onEdit={editAddressAt}
                onDelete={deleteAddressAt}
                onChange={(val) => setEditingValues((prev) => ({ ...prev, [key]: val }))}
              />
            );
          })}

          {addingNewAddress && (
            <Grid w="100%">
              <Grid.Col span={1}>
                <SystemIcon
                  system={undefined}
                  value={newAddressValue}
                  fallback={IconMapPin}
                  color="var(--mantine-color-blue-7)"
                />
              </Grid.Col>
              <Grid.Col span={11}>
                <EditableField
                  value=""
                  placeholder="Enter address"
                  inputProps={{ size: 'xs', w: '90%' }}
                  editable={true}
                  focus={addingNewAddress}
                  onChange={(v) => setNewAddressValue(v)}
                  onSubmit={async (v) => {
                    await addNewAddress(v);
                    setNewAddressValue('');
                  }}
                  onCancel={() => {
                    setAddingNewAddress(false);
                    setNewAddressValue('');
                  }}
                  onEndEditing={(info) => {
                    if (!info.value) {
                      setAddingNewAddress(false);
                      setNewAddressValue('');
                    }
                  }}
                />
              </Grid.Col>
            </Grid>
          )}
        </Stack>
      </ExpandableCard>
    </>
  );
}
